<template>
  <div v-show="roomId" class="chat-window z-0 h-[calc(100vh-60px)]">
    <!-- Header Section with Patient Info and Tabs -->
    <div class="header-section">
      <!-- Patient Header -->
      <div class="patient-header mt-12 mb-6">
        <div v-if="currentPatientData && currentPatientData.patientBasicInfo" class="patient-info">
          <div class="patient-avatar bg-[#f2f2f2] flex items-center justify-center overflow-hidden rounded-[50%]">
            <img v-if="currentPatientData.avatar" :src="currentPatientData.avatar" alt="Avatar"/>
            <div v-else-if="currentPatientData.patientBasicInfo.firstName && currentPatientData.patientBasicInfo.lastName" class="text-[#979797] text-[24px] font-medium">
              {{ currentPatientData.patientBasicInfo.firstName.charAt(0) }}{{ currentPatientData.patientBasicInfo.lastName.charAt(0) }}
            </div>
            <div v-else class="text-[#979797] text-[24px] font-medium">?</div>
          </div>
          <div class="patient-details">
            <h2 class="patient-name">{{ currentPatientData.patientBasicInfo.firstName || '' }} {{ currentPatientData.patientBasicInfo.lastName || '' }}</h2>
            <div class="patient-meta">
              <span class="patient-bio">Bio Gender: {{ currentPatientData.patientBasicInfo.gender? currentPatientData.patientBasicInfo.gender : 'N/A' }}</span>
              <span class="patient-separator">|</span>
              <span class="patient-dob">DOB: {{ formatDateOfBirth(currentPatientData.patientBasicInfo.dob? currentPatientData.patientBasicInfo.dob : 'N/A') }}</span>
            </div>
            <!-- Patient Tags -->
            <patient-tags
              v-if="currentPatientId"
              :patient-id="currentPatientId"
              @navigate-to-patient="handleNavigateToPatient"
              @go-to-chat="handleGoToChat"
            />
          </div>
        </div>
      </div>

      <!-- Tab Navigation -->
      <div ref="tabNav" class="tab-navigation">
        <div 
          v-for="tab in tabs" 
          :key="tab.id"
          class="flex flex-col items-center justify-center"
          :class="['tab-container', { active: activeTab === tab.id }]"
        >
          <div 
            :class="['tab-header', { active: activeTab === tab.id }]"
            @click="switchTab(tab.id)"
          >
            <img class="w-6 h-6" :src="tab.icon" alt="Tab Icon" />
            <span class="tab-label truncate">{{ tab.label }}</span>
          </div>
          
          <!-- Expandable Pegboard Cards within tab -->
          <div 
            v-if="activeTab === tab.id"
            class="tab-pin-cards"
          >
            <div class="pin-cards-header">
              <span class="pin-cards-count">{{ getTabCards(tab.id).length }} items</span>
            </div>
            <div class="pin-cards-list">
              <!-- Basic Info Tab Cards -->
              <template v-if="tab.id === 'basic'">
                <contact-card
                  :patient="roomId"
                  :card-data="{ id: 'contact_card', category: 'basicInfo', entity_type: 'contact' }"
                  :showFooter="false"
                  :external-variable-dialog="true"
                  @pin-card="handlePinCard"
                  @unpin-card="handleUnpinCard"
                  @edit-contact="handleEditContact"
                  @open-variable-dialog="handleOpenVariableDialog"
                />
                <demographics-card
                  :patient="roomId"
                  :card-data="{ id: 'demographics_card', category: 'basicInfo', entity_type: 'demographics' }"
                  :showFooter="false"
                  :external-variable-dialog="true"
                  @pin-card="handlePinCard"
                  @unpin-card="handleUnpinCard"
                  @open-variable-dialog="handleOpenVariableDialog"
                />
                <!-- Order Handling Preference card hidden per user request -->
                <!-- <order-handling-card
                  :patient="currentPatientId"
                  :card-data="{ id: 'order_handling_card', category: 'basicInfo', entity_type: 'handling' }"
                  :showFooter="false"
                  @pin-card="handlePinCard"
                  @unpin-card="handleUnpinCard"
                /> -->
                <internal-notes-card
                  :patient="currentPatientId"
                  :card-data="{ id: 'internal_notes_card', category: 'internalNotes', entity_type: 'internalNote' }"
                  :showFooter="false"
                  @pin-card="handlePinCard"
                  @unpin-card="handleUnpinCard"
                  @open-comments="handleOpenComments"
                  @edit-note="handleEditNote"
                />
              </template>
              
              <!-- Medical Info Tab Cards -->
              <template v-if="tab.id === 'medical'">
                <health-info-card
                  :patient="currentPatientId"
                  :card-data="{ id: 'health_info_card', category: 'Medical_Record', entity_type: 'health' }"
                  :showFooter="false"
                  :health-info="healthInfo"
                  :isLoading="healthInfoLoading"
                  :external-variable-dialog="true"
                  @pin-card="handlePinCard"
                  @unpin-card="handleUnpinCard"
                  @add-new="openHealthInfoDialog"
                  @open-variable-dialog="handleOpenVariableDialog"
                />
                <medication-card
                  :patient="currentPatientId"
                  :card-data="{ id: 'medication_card', category: 'Medical_Record', entity_type: 'medication' }"
                  :showFooter="false"
                  :medications="allMedications"
                  :isLoading="medicationPagination.isLoading"
                  :external-variable-dialog="true"
                  @pin-card="handlePinCard"
                  @unpin-card="handleUnpinCard"
                  @add-new="openMedicationFormDialog"
                  @edit="handleEditMedication"
                  @discontinue="handleDiscontinueMedication"
                  @reconcile="handleReconcile"
                  @open-variable-dialog="handleOpenVariableDialog"
                />
                <allergy-card
                  :patient="currentPatientId"
                  :card-data="{ id: 'allergy_card', category: 'Medical_Record', entity_type: 'allergy' }"
                  :showFooter="false"
                  :allergies="allergies"
                  :external-variable-dialog="true"
                  @pin-card="handlePinCard"
                  @unpin-card="handleUnpinCard"
                  @edit="handleEditAllergy"
                  @add-new="openAllergyFormDialog"
                  @reconcile="handleReconcile"
                  @open-variable-dialog="handleOpenVariableDialog"
                />
                <!-- Encounter Notes card temporarily hidden -->
                <notes-card
                  v-if="false"
                  :patient="currentPatientId"
                  :card-data="{ id: 'notes_card', category: 'Medical_Record', entity_type: 'notes' }"
                  :showFooter="false"
                  @pin-card="handlePinCard"
                  @unpin-card="handleUnpinCard"
              />
                <document-card
                  :patient="currentPatientId"
                  :patient-id="currentPatientId"
                  :card-data="{ id: 'document_card', category: 'Medical_Record', entity_type: 'document' }"
                  :documents="patientDocuments"
                  :isLoading="documentsLoading"
                  :showFooter="false"
                  @pin-card="handlePinCard"
                  @unpin-card="handleUnpinCard"
                />
              </template>
              
              <!-- Calendar Tab Cards -->
              <template v-if="tab.id === 'calendar'">
                <div v-if="appointmentsLoading || tasksLoading" class="tab-loading-state">
                  <div class="tab-loading-spinner"></div>
                  <p class="tab-loading-message">Loading calendar...</p>
                </div>
                <div v-else-if="isCalendarEmpty" class="tab-empty-state">
                  <img :src="require('@/assets/MyPatient/empty_calendar.svg')" alt="No events" class="tab-empty-illustration" />
                  <p class="tab-empty-message">There is no event for this patient yet.</p>
                </div>
                <template v-else>
                  <appointment-card
                    v-for="appointment in appointments"
                    :key="'apt_' + appointment.id"
                    :patient="currentPatientId"
                    :appointment="appointment"
                    :card-data="{ id: 'apt_card_' + appointment.id, category: 'Calendar', entity_type: 'appointment', entity_id: appointment.id }"
                    :showFooter="false"
                    @pin-card="handlePinCard"
                    @unpin-card="handleUnpinCard"
                    @edit-appointment="handleEditAppointment"
                  />
                  <task-card
                    v-for="task in tasks"
                    :key="'task_' + task.task_id"
                    :patient="currentPatientId"
                    :task="task"
                    :card-data="{ id: 'task_card_' + task.task_id, category: 'Calendar', entity_type: 'task', entity_id: task.task_id }"
                    :showFooter="false"
                    @pin-card="handlePinCard"
                    @unpin-card="handleUnpinCard"
                    @edit-task="handleEditTask"
                  />
                </template>
              </template>
              
              <!-- Billing Tab Cards -->
              <template v-if="tab.id === 'billing'">
                <div v-if="billingOrdersLoading" class="tab-loading-state">
                  <div class="tab-loading-spinner"></div>
                  <p class="tab-loading-message">Loading billing...</p>
                </div>
                <div v-else-if="isBillingEmpty" class="tab-empty-state">
                  <img :src="require('@/assets/MyPatient/empty_billing.svg')" alt="No billing" class="tab-empty-illustration" />
                  <p class="tab-empty-message">There is no billing for this patient yet.</p>
                </div>
                <template v-else>
                  <billing-card
                    v-if="false"
                    :patient="currentPatientId"
                    :card-data="{ id: 'billing_card', category: 'Billing', entity_type: 'invoice' }"
                    :showFooter="false"
                    @pin-card="handlePinCard"
                    @unpin-card="handleUnpinCard"
                  />
                  <billing-order-card
                    :patient="currentPatientId"
                    :patient-id="currentPatientId"
                    :card-data="{ id: 'billing_order_card', category: 'Billing', entity_type: 'order' }"
                    :billing-orders="billingOrders"
                    :isLoading="billingOrdersLoading"
                    :showFooter="false"
                    @pin-card="handlePinCard"
                    @unpin-card="handleUnpinCard"
                  />
                  <sample-card
                    v-for="sampleCardContent in sampleCards"
                    :key="'sample_' + sampleCardContent.id"
                    :patient="currentPatientId"
                    :sample-card="sampleCardContent"
                    :card-data="{ id: sampleCardContent.id, category: 'Billing', entity_type: 'sample', entity_id: sampleCardContent.sampleId }"
                    :showFooter="false"
                    @pin-card="handlePinCard"
                    @unpin-card="handleUnpinCard"
                  />
                </template>
              </template>
              
              <div v-if="getTabCards(tab.id).length === 0" class="no-pins">
                No items available
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Messages Container -->
    <div ref="messagesContainer" class="messages-container" @scroll="handleScroll">
      <div v-if="roomMessages.length > 0">
        <!-- Loading more indicator -->
        <div v-if="isLoadingMore" class="loading-more-container">
          <div class="spinner-small"></div>
          <span>Loading more messages...</span>
        </div>

        <div class="messages-list">
          <template v-for="(item, index) in roomMessages" :key="index">
            <!-- Failed message -->
            <MessageNotSent
              v-if="item.failed"
              :message-text="item.msg"
              :sender-name="getSenderName(item)"
              :timestamp="item.ts"
            />
            <!-- Normal message -->
            <MessageUnit
              v-else
              :message-data="item"
              :rc_user_id="rc_user_id"
              :patientInfo="currentPatientData"
              :message-type="item.t"
              @edit-appointment="handleEditAppointment"
              @edit-task="handleEditTask"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Message Input -->
    <div class="w-full px-12 pb-8 pt-6 relative">
      
      <div class="flex flex-col message-outer rounded-[5px]">
        <div class="bg-[#f2f2f2] px-4 py-2 flex items-center justify-between rounded-t-[5px]">
          <div class="flex">          
            <!-- Other actions -->
            <div 
              v-for="(action, index) in quick_actions" 
              :key="action.id" 
              class="flex items-center cursor-pointer hover:bg-[#00487914] pr-4 rounded-[5px] px-[2px]" 
              @click="handleQuickAction(action.id)" 
              @mouseenter="handleQuickActionEnter(action)" 
              @mouseleave="handleQuickActionLeave(action)"
            >
              <quick-action-icon :iconId="action.id" :isHovered="action.isHovered"/>
              <div 
                class="overflow-hidden ml-1 whitespace-nowrap"
                :style="{
                  maxWidth: action.showLabel ? '200px' : '0px',
                  transition: action.isLeaving ? 'max-width 0.5s ease-out 0.8s' : 'max-width 0.5s ease-out'
                }"
              >
                <span class="text-[#004879] underline">{{ action.label }}</span>
              </div>
            </div>
          </div>
        </div>
        <div 
          class="bg-white px-4 py-3 rounded-b-[5px] flex flex-col gap-3 relative"
          :class="droppedImages.length > 0 ? 'min-h-[180px]' : 'min-h-[100px]'"
          @dragover="handleDragOver"
          @dragleave="handleDragLeave"
          @drop="handleDrop"
        >
          <!-- Drag overlay -->
          <div 
            v-if="isDraggingOver"
            class="absolute inset-0 bg-[#004879] bg-opacity-10 border-2 border-dashed border-[#004879] rounded-b-[5px] flex items-center justify-center z-10 pointer-events-none"
          >
            <div class="text-[#004879] text-[16px] font-medium">Drop images here</div>
          </div>
          
          <textarea
            v-model="newMessage"
            placeholder="Type here to reply ..."
            class="flex-1 border-none outline-none text-[16px] text-[#333333] tracking-[0.15px] leading-[24px] h-[60px] resize-none overflow-y-auto"
            @keydown.enter.prevent="handleEnterKey"
            @paste="handlePaste"
          ></textarea>
          
          <!-- Image preview section -->
          <div 
            v-if="droppedImages.length > 0"
            class="flex gap-2 overflow-x-auto pb-2 pt-2"
            style="scrollbar-width: thin; scrollbar-color: #c4c4c4 transparent;"
          >
            <div
              v-for="image in droppedImages"
              :key="image.id"
              class="relative flex-shrink-0 w-[80px] h-[80px] rounded-[8px] border border-[#e5e7eb] group"
            >
              <img 
                :src="image.preview" 
                :alt="image.file.name"
                class="w-full h-full object-cover rounded-[8px]"
                :class="{ 'opacity-50': image.uploading }"
              />
              
              <!-- Uploading spinner -->
              <div
                v-if="image.uploading"
                class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-[8px]"
              >
                <div class="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
              
              <!-- Upload failed indicator -->
              <div
                v-if="image.uploadFailed"
                class="absolute inset-0 flex items-center justify-center bg-red-500 bg-opacity-30 rounded-[8px]"
              >
                <span class="text-white text-[20px] font-bold">!</span>
              </div>
              
              <!-- Remove button -->
              <button
                v-if="!image.uploading"
                class="absolute -top-[4px] -right-[4px] w-5 h-5 bg-[#ef4444] rounded-full flex items-center justify-center text-white text-[12px] font-bold hover:bg-[#dc2626] transition-colors shadow-md"
                title="Remove image"
                @click="removeImage(image.id)"
              >
                ×
              </button>
            </div>
          </div>
          
          <div class="flex gap-2 items-center justify-end">
            <!-- <MessageChannelSelector
              v-model="selectedChannel"
              @change="handleChannelChange"
            /> -->
            <ResponsiveButton
              class="chat-send-button"
              button_type="filled"
              built_in_theme="dark"
              size="small"
              :button_border_radius="'15px'"
              :display_name="isSendingMessage ? 'Sending...' : 'Send'"
              :disabled="(!newMessage.trim() && droppedImages.length === 0) || isSendingMessage"
              @click-button="sendMessage"
            >
              <template #prefix>
                <img
                  v-if="!isSendingMessage"
                  src="@/assets/MyPatient/arrow_upward.svg"
                  alt="Send"
                  class="chat-send-icon"
                />
                <div v-else class="chat-send-spinner"></div>
              </template>
            </ResponsiveButton>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Dialogs -->
    <ScheduleDetail
      :visible="showScheduleDetail"
      :active-tab="scheduleActiveTab"
      :hide-switch-tab="true"
      :disable-patient-selector="true"
      @close="closeScheduleDetail"
    />

    <send-document-dialog
      :visible="showSendDocumentDialog"
      :roomId="roomId"
      :clinicId="String($store.state.login.userInfo.clinic_id || '')"
      :patientId="currentPatientId"
      @close="showSendDocumentDialog = false"
      @send="handleSendDocument"
    />

    <new-order-dialog
      :visible="showNewOrderDialog"
      :roomId="roomId"
      :patientId="Number(currentPatientId) || null"
      :customerId="Number(userInfo.customer_id) || null"
      :isClinic="userInfo.user_roles.includes('clinic') || false"
      :isUsCustomer="isUsCustomer"
      :clinicId="Number(userInfo.clinic_id) || null"
      @close="showNewOrderDialog = false"
      @send="handleNewOrder"
    />

    <add-patient-dialog
      :visible="showAddPatientDialog"
      @close="showAddPatientDialog = false"
      @add="handleAddPatient"
    />
    
    <coming-soon-dialog
      :visible="showComingSoonDialog"
      @close="showComingSoonDialog = false"
    />

    <medication-form-dialog
      :visible="showMedicationFormDialog"
      :patientInfo="currentPatientData"
      :medication="editingMedication"
      @close="handleMedicationFormClose"
      @save="handleMedicationSave"
    />

    <medications-list-dialog
      :visible="showMedicationsListDialog"
      :patientId="currentPatientId"
      :medications="allMedications"
      :total="medicationPagination.total"
      :page-size="medicationPagination.pageSize"
      :current-page="medicationPagination.currentPage"
      :is-loading="medicationPagination.isLoading"
      @close="showMedicationsListDialog = false"
      @add-new="openMedicationFormDialog"
      @page-change="handleMedicationPageChange"
      @page-size-change="handleMedicationPageSizeChange"
      @edit="handleEditFromListDialog"
      @deleted="handleMedicationDeleted"
    />
    <allergy-form-dialog
      :visible="showAllergyFormDialog"
      :patientInfo="currentPatientData"
      :allergy="editingAllergy"
      @close="handleAllergyFormClose"
      @save="handleAllergyFormSave"
    />

    <allergies-list-dialog
      :visible="showAllergiesListDialog"
      :allergies="allergies"
      :patientId="currentPatientId"
      @close="showAllergiesListDialog = false"
      @add-new="openAllergyFormDialog"
      @edit="handleEditFromAllergyListDialog"
      @deleted="handleAllergyDeleted"
    />

    <health-info-dialog
      :visible="showHealthInfoDialog"
      :patientInfo="currentPatientData"
      @close="handleHealthInfoDialogClose"
      @save="handleHealthInfoSave"
    />

    <reconcile-dialog
      :visible="showReconcileDialog"
      :new-medications="reconcileData.newMedications"
      :new-allergies="reconcileData.newAllergies"
      @close="showReconcileDialog = false"
      @add-selected="handleReconcileAddSelected"
    />
    <internal-notes-dialog
      :visible="showInternalNotesDialog"
      :patientId="currentPatientId"
      :initialNoteId="noteToExpand || noteToEdit"
      :initialShowComments="showCommentsForNote"
      :initialEditMode="!!noteToEdit"
      @close="handleCloseInternalNotesDialog"
    />
    
    <EditContactAddressDialog
      v-model:visible="showEditContactDialog"
      :patient-id="currentPatientId"
      :room-id="roomId"
      @saved="handleContactSaved"
    />

    <EditPhoneNumberDialog
      v-model:visible="showEditPhoneDialog"
      :patient-id="currentPatientId"
      @saved="handlePhoneSaved"
    />

    <MissingEmailDialog
      v-model:visible="showMissingEmailDialog"
    />

    <PatientWorkflowDialog
      :visible="showWorkflowDialog"
      :patient-id="currentPatientId"
      @close="showWorkflowDialog = false"
      @preview="handleWorkflowPreview"
      @toggle-pause="handleWorkflowTogglePause"
      @remove="handleWorkflowRemove"
    />

    <PatientVariableDialog
      v-model:visible="showPtVariablesDialog"
      :ptVariables="currentPtVariables"
      :patientId="currentPatientId"
    />
  </div>
</template>

<script>
import { useStore } from 'vuex';
import MessageUnit from './MessageUnit.vue'
import MessageNotSent from './MessageNotSent.vue'
import PinCard from './pinCard.vue'
import ScheduleDetail from '@/views/my-calendar/sections/schedule/ScheduleDetail.vue'
import SendDocumentDialog from './SendDocumentDialog.vue'
import AddPatientDialog from './AddPatientDialog.vue'
import ComingSoonDialog from './ComingSoonDialog.vue'
import InternalNotesDialog from './InternalNotesDialog.vue'
import NewOrderDialog from './NewOrderDialog.vue'
import MedicationFormDialog from './MedicationFormDialog.vue'
import MedicationsListDialog from './MedicationsListDialog.vue'
import AllergyFormDialog from './AllergyFormDialog.vue'
import AllergiesListDialog from './AllergiesListDialog.vue'
import HealthInfoDialog from './HealthInfoDialog.vue'
import ReconcileDialog from './ReconcileDialog.vue'
import { fakeReconcileData } from './data/reconcile.js'
import RocketChatService from '@/services/rocketChatService'
import wsService from "@/services/websocketService.js";
import { v4 as uuidv4 } from 'uuid';
import { updateMedicationRecord, deleteAllergyRecord } from '@/api/medicationApi';
import { notify } from '@/utils/notify';
import { batchUploadChatImagesApi, deleteChatImageApi, createChatImagesFormData } from '@/api/patientFiles';

// Quick Actions 
import QuickActionIcon from './QuickActionIcon.vue'
import PatientWorkflowDialog from '../my-workbench-new/workflow/Dialogs/PatientWorkflowDialog.vue'

// Import pegboard card components
import ContactCard from '../../components/pegboard-cards/ContactCard.vue';
import DemographicsCard from '../../components/pegboard-cards/DemographicsCard.vue';
import AllergyCard from '../../components/pegboard-cards/AllergyCard.vue';
import HealthInfoCard from '../../components/pegboard-cards/HealthInfoCard.vue';
import NotesCard from '../../components/pegboard-cards/NotesCard.vue';
import OrderHandlingCard from '../../components/pegboard-cards/OrderHandlingCard.vue';
import AppointmentCard from '../../components/pegboard-cards/AppointmentCard.vue';
import TaskCard from '../../components/pegboard-cards/TaskCard.vue';
import BillingCard from '../../components/pegboard-cards/BillingCard.vue';
import BillingOrderCard from '../../components/pegboard-cards/BillingOrderCard.vue';
import MedicationCard from '../../components/pegboard-cards/MedicationCard.vue';
import SampleCard from '../../components/pegboard-cards/SampleCard.vue';
import InternalNotesCard from '../../components/pegboard-cards/InternalNotesCard.vue';
import DocumentCard from '../../components/pegboard-cards/DocumentCard.vue';
import PatientVariableDialog from '../../components/pegboard-cards/PatientVariableDialog.vue';
import PatientTags from './PatientTags.vue';
import EditContactAddressDialog from './EditContactAddressDialog.vue';
import MessageChannelSelector from './MessageChannelSelector.vue';
import EditPhoneNumberDialog from './EditPhoneNumberDialog.vue';
import MissingEmailDialog from './MissingEmailDialog.vue';
import { mapGetters, mapActions, mapState } from 'vuex';
import { useMyPatientsStore } from '@/store/helpers';
import {fakeOrderStatusMessage} from '@/utils/patientUtils'
import { getChannelReadinessApi } from '@/api/rocketChat';
import { getPatientByIdApi } from '@/api/patient';
import { buildPatientSession } from '@/utils/va-order-test/buildPatientSession';

export default {
  name: 'ChatWindow',
  components: {
    MessageUnit,
    MessageNotSent,
    PinCard,
    ScheduleDetail,
    SendDocumentDialog,
    AddPatientDialog,
    ComingSoonDialog,
    InternalNotesDialog,
    NewOrderDialog,
    MedicationFormDialog,
    MedicationsListDialog,
    AllergyFormDialog,
    AllergiesListDialog,
    HealthInfoDialog,
    ReconcileDialog,
    QuickActionIcon,
    PatientWorkflowDialog,
    ContactCard,
    DemographicsCard,
    NotesCard,
    AppointmentCard,
    TaskCard,
    BillingCard,
    BillingOrderCard,
    MedicationCard,
    SampleCard,
    AllergyCard,
    HealthInfoCard,
    OrderHandlingCard,
    InternalNotesCard,
    DocumentCard,
    PatientVariableDialog,
    PatientTags,
    EditContactAddressDialog,
    MessageChannelSelector,
    EditPhoneNumberDialog,
    MissingEmailDialog
  },
  props: {
    roomId: {
      type: [String, null],
      required: true
    },
    patientId: {
      type: [String, null],
      default: null,
      required: false
    }
  },
  setup() {
    const { patientInfoStore } = useMyPatientsStore()
    
    // Helper function to get pegboard card ID
    const getPegboardCardId = (cardData) => {
      return patientInfoStore.getters.getPegboardCardId(cardData);
    };
    
    // Helper function to check if card is pinned
    const isCardPinned = (cardData) => {
      return patientInfoStore.getters.isCardPinned(cardData);
    };
    
    // Helper to get patient by ID
    const getPatientById = (id) => {
      return patientInfoStore.getters.getPatientById(id);
    };
    
    return {
      getPegboardCardId,
      isCardPinned,
      getPatientById,
      patientInfoStore
    };
  },
  data() {
    return {
      activeTab: 'none',
      showPinCards: false,
      markAsReplied: false,
      newMessage: '',
      isSendingMessage: false, // Prevent duplicate message sending
      isFetchingPatientData: false, // Prevent duplicate API calls when switching patients
      pendingPatientId: null, // Track latest requested patient ID during fetch
      isLoadingMessages: false, // Prevent duplicate message loading during room switch
      isLoadingMore: false, // Loading more messages via scroll
      messagesPerPage: 50,
      pendingRoomId: null, // Track latest requested room ID during message load
      showScheduleDetail: false,
      scheduleActiveTab: 'appointment', // 'appointment' or 'task'
      showSendDocumentDialog: false,
      showAddPatientDialog: false,
      showComingSoonDialog: false,
      showInternalNotesDialog: false,
      noteToExpand: null,
      noteToEdit: null,
      showCommentsForNote: false,
      showNewOrderDialog: false,
      showWorkflowDialog: false,
      showMedicationFormDialog: false,
      showPtVariablesDialog: false,
      currentPtVariables: [],
      showMedicationsListDialog: false,
      editingMedication: null,
      // Track if form was opened from list dialog (should return to list on close)
      medicationFormFromList: false,
      medicationPagination: {
        total: 0,
        currentPage: 1,
        pageSize: 5,
        isLoading: false
      },
      // Allergy dialog state
      showAllergyFormDialog: false,
      showAllergiesListDialog: false,
      allergyFormFromList: false,
      editingAllergy: null,
      // Health Info dialog state
      showHealthInfoDialog: false,
      // Reconcile dialog state
      showReconcileDialog: false,
      reconcileData: { ...fakeReconcileData },
      showEditContactDialog: false,
      showOtherActionsPopup: false,
      hidePopupTimeoutId: null,
      // Message channel selector
      selectedChannel: 'in_app', // 'in_app', 'sms', 'email'
      showEditPhoneDialog: false,
      showMissingEmailDialog: false,
      RocketChatService,
      token:'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOjU0Njc0LCJ1c2VyX3Blcm1pc3Npb24iOiIyMTAzMEY4MDAyIiwiY3VzdG9tZXJfaWQiOjk5OTk5NywiY2xpbmljX2lkIjoxMDEzNiwib2xkX2NsaW5pY19pZCI6bnVsbCwicGF0aWVudF9pZCI6bnVsbCwiaW50ZXJuYWxfdXNlcl9pZCI6bnVsbCwiaW50ZXJuYWxfdXNlcl9uYW1lIjpudWxsLCJpbnRlcm5hbF91c2VyX3JvbGUiOm51bGwsInJvbGUiOiJjdXN0b21lciIsImN1c3RvbWVyX2xpc3QiOm51bGwsInNlc3Npb25faWQiOm51bGwsImVtYWlsX2xvZ19pbl9pZCI6InRlc3RAdmlicmFudC1hbWVyaWNhLmNvbSIsImJldGFfcHJvZ3JhbV9lbmFibGVkIjp0cnVlLCJiZXRhX3Byb2dyYW1zIjpbImFkdmFuY2VkX2F1dG9fcmVkcmF3X2JldGExIiwiYXV0b19yZWRyYXciLCJuZXdfYmlsbGluZ19tb2R1bGVfYmV0YTEiLCJnZW5lcmFsX2JldGExIiwiY2xvdWRfY2hhcmdpbmciLCJyZXNfYmV0YTIiLCJibG9vZF9jb25jaWVyZ2UiLCJudXRyaXByb3hfcmVmaWxsX2N1c3RvbWVyIiwidGlja2JvbmVfY3VzdG9tZXIiLCJ1dGlfem9vbWVyX2N1c3RvbWVyIiwib3JkZXJfY2xvdWQiXSwidXNlcl9pZCI6NTQ2NzQsInVzZXJfcm9sZXMiOlsiY2xpbmljIiwiY3VzdG9tZXIiXSwiaWF0IjoxNzU2MzMzNzY5LCJleHAiOjE3NTYzNDM3Njl9.uUy9_-ziLx0NS79YoBvtKyapYLdHvUItS1yeXTBXl-8',
      currentPatient: {
        name: 'Maximiliana Montmoresy',
        bioGender: 'Male',
        dob: 'Jan-12-1978'
      },
      droppedImages: [], // Array to store dropped image files with preview URLs
      isDraggingOver: false, // Track if user is dragging over the drop zone
      quick_actions:[
        {
          id: 'send_task',
          label: 'Send Task',
          isHovered: false,
          showLabel: false,
          isLeaving: false,
          collapseTimer: null,
        },
        {
          id: 'create_appointment',
          label: 'Create Appointment',
          isHovered: false,
          showLabel: false,
          isLeaving: false,
          collapseTimer: null,
        },
        {
          id: 'order',
          label: 'Order',
          isHovered: false,
          showLabel: false,
          isLeaving: false,
          collapseTimer: null,
        },
        {
          id: 'send_document',
          label: 'Send Document',
          isHovered: false,
          showLabel: false,
          isLeaving: false,
          collapseTimer: null,
        },
        {
          id: 'workflow',
          label: 'Workflow',
          isHovered: false,
          showLabel: false,
          isLeaving: false,
          collapseTimer: null,
        },
        {
          id: 'internal_notes',
          label: 'Internal Notes',
          isHovered: false,
          showLabel: false,
          isLeaving: false,
          collapseTimer: null,
        },
        {
          id: 'encounter_notes',
          label: 'Encounter Notes',
          isHovered: false,
          showLabel: false,
          isLeaving: false,
          collapseTimer: null,
        },
      ],
      other_actions: [
        {
          id: 'internal_notes',
          label: 'Internal Notes',
          action: 'internal_notes'
        },
        {
          id: 'erx',
          label: 'eRx',
          action: 'erx'
        },
        {
          id: 'encounter_notes',
          label: 'Encounter Notes',
          action: 'encounter_notes'
        }
      ],
      tabs: [
        {
          id: 'basic',
          label: 'Basics',
          icon: require('@/assets/MyPatient/basic_info.svg'),
        },
        {
          id: 'medical',
          label: 'Health',
          icon: require('@/assets/MyPatient/medical_information.svg')
        },
        {
          id: 'calendar',
          label: 'Calendar',
          icon: require('@/assets/MyPatient/input_appointment.svg')
        },
        {
          id: 'billing',
          label: 'Billing',
          icon: require('@/assets/MyPatient/billing.svg')
        }
      ],
      pinCards: {},
      messages: [],
      
      // Patient information from patients store
      patientInfo: null,
      fakeOrderStatusMessage: fakeOrderStatusMessage
    }
  },
  computed: {
    ...mapState('login', ['userInfo']),
    ...mapGetters('myPatients/rooms', ['getPatientByRoomId']),
    
    isUsCustomer() {
      if (!this.userInfo) return false;
      const currencyName = (this.$store.state.currency?.name || '').toLowerCase();
      const customerCountry = (this.userInfo.customer_country || '').toLowerCase();
      if (!currencyName || !customerCountry) return false;
      return currencyName === 'usd' && ['united states', 'canada', 'us', 'ca'].includes(customerCountry);
    },
    rc_user_id() {
      return this.$store.getters['login/rcUserId'];
    },
    
    currentPatientId() {
      // Get patient ID from the current patient data
      const p = this.getPatientByRoomId(this.roomId);
      return p?.patientBasicInfo?.patientId || null;
    },
    
    currentPatientData() {
      return this.getPatientByRoomId(this.roomId) || {};
    },
    currentPatientClientId() {
      const patientBasicInfo = this.currentPatientData?.patientBasicInfo || {};
      return (
        patientBasicInfo.clientId ||
        patientBasicInfo.client_id ||
        patientBasicInfo.customerId ||
        patientBasicInfo.customer_id ||
        this.$store.state.login.userInfo?.customer_id ||
        ""
      );
    },
    appointments() {
      // Use the shared appointments state that's fetched from calendar API
      return this.patientInfoStore.state.currentPatientAppointments.value || [];
    },
    tasks() {
      // Use the shared tasks state that's fetched from calendar API
      return this.patientInfoStore.state.currentPatientTasks.value || [];
    },
    sampleCards() {
      const p = this.getPatientById(this.currentPatientId) || {};
      return (p.Billing && p.Billing.Sample) ? p.Billing.Sample : [];
    },
    currentTabPinCards() {
      return this.pinCards[this.activeTab] || [];
    },
    pinCardsCount() {
      return this.currentTabPinCards.filter(card => card.isPinned).length;
    },
    roomMessages() {
      return this.$store.getters['myPatients/message/getRoomMessages'](this.roomId) || [];
    },
    totalMessages() {
      return this.$store.getters['myPatients/message/getTotalMessageCount'](this.roomId);
    },
    currentMessageCount() {
      return this.roomMessages.length;
    },
    hasMoreMessages() {
      return this.$store.getters['myPatients/message/hasMoreMessages'](this.roomId);
    },
    allMedications() {
      return this.patientInfoStore.state.currentPatientMedications.value || [];
    },
    allergies() {
      return this.patientInfoStore.state.currentPatientAllergies.value || [];
    },
    healthInfo() {
      return this.patientInfoStore.state.currentPatientHealthInfo.value || null;
    },
    billingOrders() {
      return this.patientInfoStore.state.currentPatientBillingOrders.value || [];
    },
    billingOrdersLoading() {
      return this.patientInfoStore.state.billingOrdersLoading.value || false;
    },
    patientDocuments() {
      return this.patientInfoStore.state.currentPatientDocuments.value || [];
    },
    documentsLoading() {
      return this.patientInfoStore.state.documentsLoading.value || false;
    },
    healthInfoLoading() {
      return this.patientInfoStore.state.healthInfoLoading.value || false;
    },
    appointmentsLoading() {
      return this.patientInfoStore.state.appointmentsLoading.value || false;
    },
    tasksLoading() {
      return this.patientInfoStore.state.tasksLoading.value || false;
    },
    isCalendarEmpty() {
      return !this.appointmentsLoading && !this.tasksLoading && this.appointments.length === 0 && this.tasks.length === 0;
    },
    isBillingEmpty() {
      return !this.billingOrdersLoading && this.billingOrders.length === 0 && this.sampleCards.length === 0;
    },
  },
  watch: {
    // Watch for activeTab changes to fetch medications when medical tab is activated
    activeTab: {
      immediate: false,
      async handler(newVal) {
      }
    },
    // Watch for changes in room messages from store and auto-scroll to bottom
    roomMessages: {
      handler(newMessages, oldMessages) {
        // Detect new messages arriving (not from initial load or user send)
        if (oldMessages && newMessages.length > oldMessages.length) {
          // New message arrived, increment total
          const newCount = newMessages.length - oldMessages.length;
          for (let i = 0; i < newCount; i++) {
            this.$store.dispatch('myPatients/message/incrementRoomTotal', this.roomId);
          }
        }
        this.scrollToBottom()
      },
      deep: true
    },
    // Watch for patient ID changes to reload pegboard data
    currentPatientId: {
      immediate: true,
      async handler(newPatientId, oldPatientId) {
        if (newPatientId && newPatientId !== oldPatientId) {
          // If already fetching, queue the latest patient ID and clear pegboard immediately
          if (this.isFetchingPatientData) {
            this.pendingPatientId = newPatientId;
            // Clear pegboard immediately so stale data is never shown
            this.$store.commit('myPatients/patientInfo/UPDATE_PEGBOARD_CARDS_ORDER', []);
            this.$store.commit('myPatients/patientInfo/SET_PEGBOARD_LOADING', true);
            console.log('⏭️ Patient data fetch in progress, queuing latest request');
            return;
          }
          
          await this.fetchAllPatientData(newPatientId);
        }
      }
    },
    
    // Watch for roomId changes from parent component
    roomId: {
      async handler(newRoomId, oldRoomId) {
        console.log('🔄 RoomId changed');

        if (!newRoomId || typeof newRoomId !== 'string') {
          console.log('⚠️ Invalid new roomId, skipping room switch');
          return;
        }

        if (newRoomId === oldRoomId) {
          console.log('😅 Same roomId, no action needed');
          return;
        }

        try {
          // 1. Clear previous patient info from sessionStorage when switching rooms
          sessionStorage.removeItem("patient");
          this.$store.dispatch("orderTest/initializeStore");

          // 2. Patient data will be fetched automatically by currentPatientId watcher
          console.log('✅ Room switched to:', newRoomId);

          // 3. Load history for new room
          console.log('📚 Loading history for new room');
          await this.loadHistory(newRoomId);
          this.scrollToBottom();

        } catch (error) {
          console.error('❌ Error during room switch:', error);
        }
      },
      immediate: false // Don't trigger on initial mount
    }
  },
  async mounted() {
    // 1. Add global click listener
    document.addEventListener('click', this.onGlobalClick);
    
    // 2. Initialize fake reconcile data for testing
    this.initializeFakeReconcileData();
    
    // 3. If roomId is provided, load room-specific data
    if (!this.roomId || typeof this.roomId !== 'string') {
      console.log('🚫 No roomId provided in ChatWindow');
      return;
    }
    
    console.log('✅ Valid roomId received from parent');
    
    // 4. Load message history for the initial room
    await this.loadHistory(this.roomId);
    this.scrollToBottom();
    
    // 5. Get pegboard data for the initial room
    await this.getPegBoardList();
  },
  unmounted() {
    document.removeEventListener('click', this.onGlobalClick);
  },
  // computed: {
  //   currentTabPinCards() {
  //     return this.pinCards[this.activeTab] || []
  //   },
  //   pinCardsCount() {
  //     return this.currentTabPinCards.filter(card => card.isPinned).length
  //   },
  //   // Get room messages from Vuex store
  //   roomMessages() {
  //     return this.$store.getters['myPatients/message/getRoomMessages'](this.rid) || []
  //   }
  // },
  methods: {
    ...mapActions('myPatients/patientInfo', [
      'pinCard', 
      'unpinCard', 
      'updateContactInfo', 
      'updateDemographics'
    ]),

    // Handle navigation to patient from tags overlay
    handleNavigateToPatient(patient) {
      // Emit event to parent to handle patient navigation
      this.$emit('navigate-to-patient', patient);
    },

    // Handle "Go to chat" from the tag patient list modal
    handleGoToChat({ patientId }) {
      this.$emit('go-to-chat', { patientId });
    },

    // Close active tab dropdown if clicking outside the 4 tabs area
    onGlobalClick(event) {
      try {
        // If no tab open, ignore
        if (!this.activeTab || this.activeTab === 'none') return;
        const tabsEl = this.$refs.tabNav;
        if (!tabsEl) return;
        const clickedInsideTabs = tabsEl.contains(event.target);
        if (!clickedInsideTabs) {
          this.activeTab = 'none';
        }
      } catch (e) {
        // no-op safeguard
      }
    },
    
    // Fetch patient information based on roomId
    fetchPatientInfo(roomId) {
      if (!roomId) {
        this.patientInfo = null;
        return;
      }
      
      try {
        const patient = this.getPatientByRoomId(roomId);
        if (patient) {
          this.patientInfo = patient;
          
          // Update patientInfo store with contact information
          if (patient.patientBasicInfo) {
            const basicInfo = patient.patientBasicInfo;
            const contactInfo = {
              phone: basicInfo.phone || '',
              email: basicInfo.email || '',
              address: basicInfo.address || '',
              preferredContact: 'Phone'
            };
            
            // Use patientId as the identifier for the store
            const patientId = basicInfo.patientId;
            
            this.updateContactInfo({
              patientId: patientId,
              contactInfo: contactInfo
            });

            // Store cleaned email/phone in orderTest/order store for checkout prefill
            const cleanEmail = (basicInfo.email || '').replace(/[\x00-\x1F\x7F]/g, '').trim();
            const cleanPhone = (basicInfo.phone || '').replace(/[\x00-\x1F\x7F]/g, '').trim();
            if (cleanEmail) {
              this.$store.dispatch('orderTest/order/setContactInfo', { method: 'email', info: cleanEmail });
            }
            if (cleanPhone) {
              this.$store.dispatch('orderTest/order/setContactInfo', { method: 'phone', info: cleanPhone });
            }

            console.log('✅ Contact info updated successfully');
            
            // Update demographics information
            const age = this.calculateAge(basicInfo.dob);
            const formattedDob = this.formatDateOfBirth(basicInfo.dob);
            
            const demographics = {
              gender: basicInfo.gender || '',
              age: age,
              dateOfBirth: basicInfo.dob || '',
              formattedDateOfBirth: formattedDob,
              race: 'Caucasian', // Hardcoded as requested
              ethnicity: 'Non-Hispanic' // Hardcoded as requested
            };
            
            console.log('👥 Updating demographics info:', demographics);
            
            this.updateDemographics({
              patientId: patientId,
              demographics: demographics
            });
            
          }
        } else {
          this.patientInfo = null;
        }
      } catch (error) {
        console.error('❌ Error fetching patient info:', error);
        this.patientInfo = null;
      }
    },

    // Convert function
    formatOrderMessage(msg) {
      const fields = msg.attachments[0].fields.reduce((acc, f) => {
        acc[f.title] = f.value;
        return acc;
      }, {});

      return {
        id: `sample_${fields.sampleId}`,
        serviceDate: new Date(fields.sampleOrderTime).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
        status: fields.orderStatus === 'new_order' ? 'Order Created' : fields.orderStatus, // map to your store status
        sampleId: fields.sampleId,
        orderTestNames: 'Test Name Test Name' // placeholder since original message does not include test names
      };
    },
    
    // Helper method to calculate age from date of birth
    calculateAge(dob) {
      if (!dob) return 0;
      
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age;
    },
    
    // Helper method to format date of birth
    formatDateOfBirth(dob) {
      if (!dob || dob === 'N/A') return 'N/A';

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      // Split the date string format "YYYY-MM-DD"
      const [year, month, day] = dob.split('-');

      // Map month number to month name (subtract 1 for zero-based index)
      const monthName = months[parseInt(month) - 1] || '';

      return `${monthName}-${day}, ${year}`;
    },
    // Helper methods for task/event formatting
    formatDate(dateStr) {
      if (!dateStr) return 'N/A'
      const date = new Date(dateStr)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${months[date.getMonth()]}-${String(date.getDate()).padStart(2, '0')}-${date.getFullYear()}`
    },
    formatTime(dateStr) {
      if (!dateStr) return 'N/A'
      const date = new Date(dateStr)
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    },
    calculateDuration(startTime, endTime) {
      if (!startTime || !endTime) return 'N/A'
      const start = new Date(startTime)
      const end = new Date(endTime)
      const diffMs = end - start
      const diffMins = Math.round(diffMs / 60000)
      return `${diffMins} min`
    },
    getMeetingTypeLabel(eventType) {
      const typeLabels = {
        'Initial Consultation': 'Initial Consultation',
        'Follow-up': 'Follow-up',
        'Annual Wellness': 'Annual Wellness',
        'Lab Results Review': 'Lab Results Review',
        'Procedure': 'Procedure'
      }
      return typeLabels[eventType] || eventType || 'Appointment'
    },

    // Get cards for a specific tab
    getTabCards(tabId) {
      switch(tabId) {
        case 'basic':
          return ['contact', 'demographics'];
        case 'medical':
          return ['medication', 'document'];
        case 'calendar':
          return ['appointments', 'tasks', 'forms', 'questionnaires'];
        case 'billing':
          return ['billing'];
        default:
          return [];
      }
    },
    
    // Handle pin/unpin card functionality
        async handlePinCard(cardData) {
      console.log('Pinning card data:', cardData);
      try {
        // Convert entity_id to string if it exists (required by backend)
        const processedCardData = {
          ...cardData,
          entity_id: cardData.entity_id ? String(cardData.entity_id) : cardData.entity_id
        };
        
        await this.$store.dispatch('myPatients/patientInfo/pinCard', {
          customer_id: this.$store.state.login.userInfo.customer_id,
          clinic_id: this.$store.state.login.userInfo.clinic_id,
          patient_id: this.currentPatientId,
          cardData: processedCardData,
        });
      } catch (error) {
        console.error('Error pinning card:', error);
      }
    },

    // Handle unpin card functionality
    async handleUnpinCard(cardData) {
      console.log('Unpinning card data:', cardData);
      
      try {
        if (!cardData) {
          throw new Error('No card data provided');
        }
        
        // Get the pegboard card ID directly from the store
        const pegboardCardId = this.$store.getters['myPatients/patientInfo/getPegboardCardId'](cardData);
        console.log('Found pegboard card ID:', pegboardCardId);
        
        // Call the store action to unpin the card
        await this.$store.dispatch('myPatients/patientInfo/unpinCard', {
          customer_id: this.$store.state.login.userInfo.customer_id,
          clinic_id: this.$store.state.login.userInfo.clinic_id,
          patient_id: this.currentPatientId,
          card_id: pegboardCardId
        });
        
        console.log('Card unpinned successfully');
      } catch (error) {
        console.error('Failed to unpin card:', error);
        throw error; // Re-throw to allow error handling in the component
      }
    },

    // Handle open comments from Internal Notes card
    handleOpenComments(noteId) {
      this.noteToExpand = noteId;
      this.showCommentsForNote = true;
      this.showInternalNotesDialog = true;
    },

    // Handle edit note from Internal Notes card
    handleEditNote(noteId) {
      this.noteToEdit = noteId;
      this.showInternalNotesDialog = true;
    },

    // Handle close internal notes dialog and reset state
    handleCloseInternalNotesDialog() {
      this.showInternalNotesDialog = false;
      this.noteToExpand = null;
      this.noteToEdit = null;
      this.showCommentsForNote = false;
    },

    async fetchAllPatientData(patientId) {
      try {
        this.isFetchingPatientData = true;
        console.log('📊 Fetching patient data for', patientId);
        
        // Set loading states immediately so spinners show before async fetches start
        this.$store.commit('myPatients/patientInfo/SET_APPOINTMENTS_LOADING', true);
        this.$store.commit('myPatients/patientInfo/SET_TASKS_LOADING', true);
        this.$store.commit('myPatients/patientInfo/SET_BILLING_ORDERS_LOADING', true);
        this.$store.commit('myPatients/patientInfo/SET_HEALTH_INFO_LOADING', true);
        this.$store.commit('myPatients/patientInfo/SET_CURRENT_PATIENT_HEALTH_INFO', null);
        
        // Clear pegboard cards immediately so loading state is visible on the right side
        this.$store.commit('myPatients/patientInfo/UPDATE_PEGBOARD_CARDS_ORDER', []);
        
        // Fetch patient data from API
        await this.getPegBoardList();
        // Fetch patient calendar ID mapping
        await this.patientInfoStore.actions.fetchPatientCalendarIdMap();
        // Reload medications and allergies from store (shared state)
        this.medicationPagination.total = 0;
        await Promise.all([
          this.fetchPatientVitals(),
          this.fetchAllMedications(),
          this.fetchAllAllergies(),
          this.fetchBillingOrders(),
          this.fetchPatientDocuments()
        ]);
        // Fetch appointments for this patient
        await this.patientInfoStore.actions.fetchPatientAppointments(patientId);
        // Fetch tasks for this patient
        await this.patientInfoStore.actions.fetchPatientTasks(patientId);
      } catch (error) {
        console.error('❌ Error reloading pegboard after patient change:', error);
      } finally {
        this.isFetchingPatientData = false;
        
        // If a new patient was requested while fetching, process it now
        if (this.pendingPatientId && this.pendingPatientId !== patientId) {
          const nextPatientId = this.pendingPatientId;
          this.pendingPatientId = null;
          console.log('🔄 Processing queued patient data fetch for', nextPatientId);
          await this.fetchAllPatientData(nextPatientId);
        }
      }
    },
    async getPegBoardList() {
      if (!this.currentPatientId) {
        return;
      }
      try {
        await this.$store.dispatch('myPatients/patientInfo/getPegboardList', {
          customer_id: this.$store.state.login.userInfo.customer_id,
          clinic_id: this.$store.state.login.userInfo.clinic_id,
          patient_id: this.currentPatientId,
        });
      } catch (error) {
        console.error('Error fetching pegboard data:', error);
      }
    },
    
    async loadHistory(targetRoomId = null) {
      // Use explicit roomId parameter to avoid race conditions
      const roomId = targetRoomId || this.roomId;
      
      if (!roomId) {
        console.log('⚠️ No roomId provided to loadHistory, skipping');
        return;
      }
      
      // If already loading, queue the latest room ID
      if (this.isLoadingMessages) {
        this.pendingRoomId = roomId;
        console.log('⏭️ Message load in progress, queuing latest request');
        return;
      }
      
      let headers = {
        'Authorization': `Bearer ${this.token}`,
      }
      
      try {
        this.isLoadingMessages = true;
        console.log('📚 Loading message history');

        // Initialize room in message store using explicit roomId
        this.$store.dispatch('myPatients/message/initializeRoom', { roomId: roomId, messages: [] });

        const response = await this.RocketChatService.get(`/messages?roomId=${roomId}&count=${this.messagesPerPage}&offset=0`, { headers});
        const messages = response.data.messages.reverse();

        // Double-check we're still on the same room before storing messages
        if (roomId === this.roomId) {
          // Store messages in Vuex store instead of local state
          this.$store.dispatch('myPatients/message/setRoomMessages', {
            roomId: roomId,
            messages: messages
          });

          // Store total message count from API
          if (response.data.total !== undefined) {
            this.$store.dispatch('myPatients/message/setRoomTotal', {
              roomId: roomId,
              total: response.data.total
            });
            console.log(`📊 Loaded ${messages.length} of ${response.data.total} total messages`);
          } else {
            console.log(`✅ Loaded ${messages.length} messages`);
          }
        } else {
          console.log('⚠️ Room changed during message load, discarding stale messages');
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        this.isLoadingMessages = false;
        
        // If a new room was requested while loading, process it now
        if (this.pendingRoomId && this.pendingRoomId !== roomId) {
          const nextRoomId = this.pendingRoomId;
          this.pendingRoomId = null;
          console.log('🔄 Processing queued message load');
          await this.loadHistory(nextRoomId);
        }
      }
    },

    async loadMoreMessages() {
      if (!this.roomId || this.isLoadingMore || !this.hasMoreMessages) return;

      this.isLoadingMore = true;

      let headers = {
        'Authorization': `Bearer ${this.token}`,
      }

      // Offset is the current message count (includes fetched + new arrived + user sent)
      const offset = this.currentMessageCount;

      console.log(`📜 Loading more messages... offset=${offset}, count=${this.messagesPerPage}`);

      try {
        const response = await this.RocketChatService.get(
          `/messages?roomId=${this.roomId}&count=${this.messagesPerPage}&offset=${offset}`,
          { headers }
        );

        if (response.data && response.data.messages) {
          const newMessages = response.data.messages.reverse();

          // Prepend new messages to the beginning of the list
          const updatedMessages = [...newMessages, ...this.roomMessages];
          this.$store.dispatch('myPatients/message/setRoomMessages', {
            roomId: this.roomId,
            messages: updatedMessages
          });

          // Update total from API (always have the newest total)
          if (response.data.total !== undefined) {
            this.$store.dispatch('myPatients/message/setRoomTotal', {
              roomId: this.roomId,
              total: response.data.total
            });
          }

          console.log(`✅ Loaded ${newMessages.length} more messages (total loaded: ${updatedMessages.length} of ${response.data.total})`);
        }
      } catch (error) {
        console.error('Failed to load more messages:', error);
      } finally {
        this.isLoadingMore = false;
      }
    },

    handleScroll(event) {
      const container = event.target;
      const scrollTop = container.scrollTop;

      // When scrolled to top (within 50px threshold)
      if (scrollTop < 50 && this.hasMoreMessages && !this.isLoadingMore && !this.isLoadingMessages) {
        // Save current scroll position to restore after loading
        const oldScrollHeight = container.scrollHeight;

        this.loadMoreMessages().then(() => {
          // Restore scroll position to maintain user's reading position
          this.$nextTick(() => {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = scrollTop + (newScrollHeight - oldScrollHeight);
          });
        });
      }
    },

    switchTab(tabId) {
      if (this.activeTab === tabId) {
        // If clicking the same active tab, collapse it
        this.activeTab = null;
      } else {
        // Switch to new tab and expand it
        this.activeTab = tabId;
      }
    },
    getTabPinCards(tabId) {
      return this.pinCards[tabId] || [];
    },
    getActiveTabLabel() {
      const tab = this.tabs.find(t => t.id === this.activeTab);
      return tab ? tab.label : '';
    },
    handleTabHover(tabId) {
      if (!this.activeTab) {
        this.activeTab = tabId;
      }
    },
    handleTabLeave(tabId) {
      // Optionally auto-close on mouse leave
      // this.activeTab = null;
    },
    handleTogglePin(cardId) {
      const card = this.currentTabPinCards.find(c => c.id === cardId)
      if (card) {
        card.isPinned = !card.isPinned
      }
    },
    handleEditCard(cardId) {
      console.log('Edit card:', cardId)
      // Implement edit functionality
    },
    handleQuickActionEnter(action) {
      // Clear any existing collapse timer
      if (action.collapseTimer) {
        clearTimeout(action.collapseTimer);
        action.collapseTimer = null;
      }
      // Immediately show hover state and label
      action.isHovered = true;
      action.isLeaving = false;
      action.showLabel = true;
    },
    handleQuickActionLeave(action) {
      // Immediately remove hover state (for icon color)
      action.isHovered = false;
      action.isLeaving = true;
      
      // Wait 0.8s, then start the slide-out transition (0.5s), then hide
      action.collapseTimer = setTimeout(() => {
        action.showLabel = false;
        action.isLeaving = false;
        action.collapseTimer = null;
      }, 1300); // 800ms wait + 500ms transition
    },
    handleQuickAction(action) {
      if (action === 'send_task') {
        this.openScheduleDetail('task')
      } else if (action === 'create_appointment') {
        this.openScheduleDetail('appointment')
      } else if (action === 'send_document') {
        this.openSendDocumentDialog()
      } else if (action === 'add_patient') {
        this.openAddPatientDialog()
      } else if (action === 'workflow') {
        this.showWorkflowDialog = true
      } else if (action === 'order') {
        this.openNewOrderDialog()
      } else if (action === 'internal_notes') {
        this.openInternalNotesDialog()
      } else if (action === 'encounter_notes') {
        const patientInfo = this.currentPatientData?.patientBasicInfo;
        if (patientInfo && patientInfo.patientId) {
          const route = this.$router.resolve({ path: '/encounter-notes', query: { patientId: patientInfo.patientId } });
          window.open(route.href, '_blank');
        }
      }
    },
    hideOtherActionsPopup() {
      // Add a small delay to allow moving to popup
      this.hidePopupTimeoutId = setTimeout(() => {
        this.showOtherActionsPopup = false;
        this.quick_actions[0].isHovered = false;
        this.hidePopupTimeoutId = null;
      }, 200);
    },
    cancelHideTimeout() {
      // Cancel the timeout if user moves to popover
      if (this.hidePopupTimeoutId) {
        clearTimeout(this.hidePopupTimeoutId);
        this.hidePopupTimeoutId = null;
      }
      // Ensure hover state stays active
      this.quick_actions[0].isHovered = true;
      this.showOtherActionsPopup = true;
    },
    handleOtherAction(action) {
      if (action === 'internal_notes') {
        this.openInternalNotesDialog();
      } else {
        this.openComingSoonDialog();
      }
      this.showOtherActionsPopup = false;
    },
    handleOtherActionAndClose(action) {
      this.quick_actions[0].isHovered = false;
      this.showOtherActionsPopup = false;
      if (action === 'encounter_notes') {
        const patientInfo = this.currentPatientData?.patientBasicInfo;
        if (patientInfo && patientInfo.patientId) {
          const route = this.$router.resolve({ path: '/encounter-notes', query: { patientId: patientInfo.patientId } });
          window.open(route.href, '_blank');
          return;
        }
      } else if (action === 'internal_notes') {
        this.openInternalNotesDialog();
        return;
      }

      // Show coming soon dialog
      this.openComingSoonDialog();
    },
    handleEditContact() {
      this.showEditContactDialog = true
    },
    async handleEditAppointment(appointment) {
      // Ensure clinic data (patients/providers) is loaded before opening edit modal
      const userInfo = this.$store.state.login.userInfo;
      const patientList = this.$store.getters['calendar/formattedPatientsList'];
      
      if ((!patientList || patientList.length === 0) && userInfo?.clinic_id) {
        await this.$store.dispatch('calendar/fetchClinic', {
          clinicId: userInfo.clinic_id
        });
      }

      // Set the appointment info in the calendar store for editing
      // Use original API format: start_time, end_time, event_title
      const startDate = new Date(appointment.start_time);
      const endDate = new Date(appointment.end_time);

      // Format time for ScheduleAppointmentPanel (hh:mma format)
      const formatTime = (date) => {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${String(minutes).padStart(2, '0')}${ampm}`;
      };

      const selectedStartTime = formatTime(startDate);
      const selectedEndTime = formatTime(endDate);
      
      // Convert to local date string to prevent timezone shift
      const year = startDate.getFullYear();
      const month = String(startDate.getMonth() + 1).padStart(2, '0');
      const day = String(startDate.getDate()).padStart(2, '0');
      const selectedDate = `${year}-${month}-${day}`; // YYYY-MM-DD format in local timezone

      // Get patient ID from patient_ids array
      const patientId = appointment.patient_ids && appointment.patient_ids.length > 0 
        ? appointment.patient_ids[0] 
        : this.currentPatientId;

      const eventInfo = {
        event_id: appointment.event_id,
        event_title: appointment.event_title || 'Appointment',
        start: startDate,
        end: endDate,
        mode: 'edit', // IMPORTANT: Set mode to 'edit' explicitly
        notes: appointment.notes || '',
        event_type: appointment.event_type || 'appointment',
        // Additional fields for ScheduleAppointmentPanel
        selectedDate: selectedDate,
        selectedStartTime: selectedStartTime,
        selectedEndTime: selectedEndTime,
        meetingType: appointment.event_type || 'appointment',
        selectedPatient: patientId, // Patient ID from patient_ids array
        selectedProviders: [], // Will be populated by modal's watcher from participants
        participants: appointment.participants || [], // Include participants for modal to process
        zoomMeetingUrl: appointment.zoom_event_id ? appointment.external_url : '',
        external_url: appointment.external_url || '',
        videoConferencingStatus: appointment.external_url ? 'checked' : 'uncheck',
        location: appointment.location || '',
        practice_id: this.$store.state.login.userInfo?.clinic_id
      };

      // Set event info BEFORE opening the dialog
      this.$store.dispatch('calendar/setCurrentEventInfo', eventInfo);

      // Then open the dialog (it will use the edit mode we just set)
      this.showScheduleDetail = true;
      this.scheduleActiveTab = 'appointment';
    },
    async handleEditTask(task) {
      // Ensure clinic data (patients/providers) is loaded before opening edit modal
      const userInfo = this.$store.state.login.userInfo;
      const patientList = this.$store.getters['calendar/formattedPatientsList'];
      
      if ((!patientList || patientList.length === 0) && userInfo?.clinic_id) {
        await this.$store.dispatch('calendar/fetchClinic', {
          clinicId: userInfo.clinic_id
        });
      }

      // Get patient ID from patient_ids array or use current patient
      const patientId = task.patient_ids && task.patient_ids.length > 0 
        ? task.patient_ids[0] 
        : this.currentPatientId;

      // Get calendar_id from patient list
      const selectedPatient = patientList.find(p => p.id === patientId);

      // Set the task info in the calendar store for editing
      const taskInfo = {
        task_id: task.task_id,
        title: task.title || '',
        description: task.description || '',
        due_date: task.due_date || task.start_date || '',
        start_date: task.start_date || task.due_date || '',
        is_completed: task.is_completed || false,
        mode: 'edit', // IMPORTANT: Set mode to 'edit' explicitly
        selectedPatient: patientId,
        calendar_id: selectedPatient?.calendar_id || null,
        practice_id: userInfo?.clinic_id
      };

      // Set task info BEFORE opening the dialog
      this.$store.dispatch('calendar/setCurrentTaskInfo', taskInfo);

      // Then open the dialog (it will use the edit mode we just set)
      this.showScheduleDetail = true;
      this.scheduleActiveTab = 'task';
    },
    handleContactSaved() {
      this.showEditContactDialog = false
    },
    async handleChannelChange(newChannel) {
      // Only check readiness for SMS and Email channels
      if (newChannel !== 'sms' && newChannel !== 'email') {
        return;
      }

      if (!this.currentPatientId) {
        return;
      }

      const clinicId = this.$store.state.login.userInfo?.clinic_id;
      if (!clinicId) {
        return;
      }

      try {
        // Call channel readiness API
        const response = await getChannelReadinessApi(
          this.currentPatientId,
          clinicId,
          newChannel
        );

        const { clinic_ready, patient_ready } = response.data || {};

        // Check clinic readiness first
        if (!clinic_ready) {
          // Show MissingEmailDialog for both SMS and Email if clinic not ready
          this.showMissingEmailDialog = true;
          this.$nextTick(() => {
            this.selectedChannel = 'in_app';
          });
          return;
        }

        // Clinic is ready, check patient readiness
        if (!patient_ready) {
          if (newChannel === 'sms') {
            // Show edit phone dialog for SMS
            this.showEditPhoneDialog = true;
            this.$nextTick(() => {
              this.selectedChannel = 'in_app';
            });
          } else if (newChannel === 'email') {
            // Show notification for Email
            notify({ type: 'warning', title: 'Patient Email Not Ready', message: 'Patient email is not configured. Please update patient contact information.' });
            this.$nextTick(() => {
              this.selectedChannel = 'in_app';
            });
          }
          return;
        }

        // Both clinic and patient are ready - channel switch is successful
        // selectedChannel is already updated by v-model
      } catch (error) {
        console.error('Failed to check channel readiness:', error);
        // Revert to in_app on error
        this.$nextTick(() => {
          this.selectedChannel = 'in_app';
        });
      }
    },
    
    // Image drop handlers
    handleDragOver(e) {
      e.preventDefault();
      e.stopPropagation();
      this.isDraggingOver = true;
    },
    
    handleDragLeave(e) {
      e.preventDefault();
      e.stopPropagation();
      this.isDraggingOver = false;
    },
    
    async handleDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      this.isDraggingOver = false;
      
      const files = Array.from(e.dataTransfer.files);
      
      // Filter only image files
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      
      if (imageFiles.length === 0) {
        notify({
          type: 'warning',
          title: 'Invalid File Type',
          message: 'Only image files are allowed'
        });
        return;
      }
      
      // Create preview URLs and upload immediately
      imageFiles.forEach(async (file) => {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const imageId = uuidv4();
          const imageObj = {
            file: file,
            preview: event.target.result,
            id: imageId,
            uploading: true,
            uploadedId: null
          };
          
          this.droppedImages.push(imageObj);
          
          // Upload immediately
          await this.uploadImage(imageObj);
        };
        reader.readAsDataURL(file);
      });
    },
    
    async handlePaste(e) {
      // Get clipboard items
      const items = e.clipboardData?.items;
      if (!items) return;
      
      // Filter image items from clipboard
      const imageItems = Array.from(items).filter(item => item.type.startsWith('image/'));
      
      if (imageItems.length === 0) return;
      
      // Prevent default paste behavior for images
      e.preventDefault();
      
      console.log('📋 Pasted', imageItems.length, 'image(s)');
      
      // Process each image and upload immediately
      imageItems.forEach(async (item) => {
        const file = item.getAsFile();
        if (!file) return;
        
        // Create preview URL
        const reader = new FileReader();
        reader.onload = async (event) => {
          const imageId = uuidv4();
          const imageObj = {
            file: file,
            preview: event.target.result,
            id: imageId,
            uploading: true,
            uploadedId: null
          };
          
          this.droppedImages.push(imageObj);
          
          // Upload immediately
          await this.uploadImage(imageObj);
        };
        reader.readAsDataURL(file);
      });
    },
    
    async uploadImage(imageObj) {
      try {
        const clinicId = String(this.$store.state.login.userInfo.clinic_id || '');
        const patientId = this.currentPatientId;
        
        // Create FormData for single image upload (batch with 1 file)
        const formData = createChatImagesFormData({
          clinicId,
          patientId,
          files: [imageObj.file]
        });
        
        console.log('📤 Uploading image:', imageObj.file.name);
        const uploadResponse = await batchUploadChatImagesApi(formData);
        
        // API returns { items: [...] } structure
        if (uploadResponse.data && uploadResponse.data.items && uploadResponse.data.items.length > 0) {
          const uploadedImage = uploadResponse.data.items[0];
          
          // Update the image object with uploaded data
          const index = this.droppedImages.findIndex(img => img.id === imageObj.id);
          if (index !== -1) {
            this.droppedImages[index].uploadedData = uploadedImage; // Store complete uploaded data
            this.droppedImages[index].uploading = false;
          }
          
          console.log('✅ Image uploaded successfully:', uploadedImage.id);
        }
      } catch (error) {
        console.error('❌ Failed to upload image:', error);
        
        // Mark upload as failed
        const index = this.droppedImages.findIndex(img => img.id === imageObj.id);
        if (index !== -1) {
          this.droppedImages[index].uploading = false;
          this.droppedImages[index].uploadFailed = true;
        }
        
        notify({
          type: 'error',
          title: 'Upload Failed',
          message: `Failed to upload ${imageObj.file.name}`
        });
      }
    },
    
    async removeImage(imageId) {
      const image = this.droppedImages.find(img => img.id === imageId);
      
      // If image has been uploaded (has uploadedData), delete from server
      if (image?.uploadedData?.id) {
        try {
          await deleteChatImageApi(image.uploadedData.id);
          console.log('🗑️ Deleted uploaded image:', image.uploadedData.id);
        } catch (error) {
          console.error('❌ Failed to delete image:', error);
          notify({
            type: 'error',
            title: 'Delete Failed',
            message: 'Failed to delete image from server'
          });
        }
      }
      
      // Remove from local array
      this.droppedImages = this.droppedImages.filter(img => img.id !== imageId);
    },
    handlePhoneSaved() {
      this.showEditPhoneDialog = false;
    },
    async openScheduleDetail(tab) {
      // Set the active tab based on the action
      this.scheduleActiveTab = tab

      // Load calendar data in background (dialog handles loading state)
      this.loadCalendarDataInBackground()

      // Initialize patient data in calendar store with number conversion
      const patientId = this.currentPatientId
      if (patientId) {
        // Convert to number to match calendar patient list format
        const patientIdNumber = Number(patientId)
        
        this.$store.dispatch('calendar/setCurrentTaskInfo', {
          mode: 'create',
          selectedPatient: patientIdNumber
        })
        this.$store.dispatch('calendar/setCurrentEventInfo', {
          mode: 'create',
          selectedPatient: patientIdNumber,
          patient_id: patientIdNumber  // Also set patient_id for compatibility with SchedulePreview
        })
      } else {
        this.$store.dispatch('calendar/setCurrentTaskInfo', {
          mode: 'create'
        })
        this.$store.dispatch('calendar/setCurrentEventInfo', {
          mode: 'create'
        })
      }

      // Show the modal immediately (dialog handles data loading internally)
      this.showScheduleDetail = true
    },
    async loadCalendarDataInBackground() {
      const userInfo = this.$store.state.login?.userInfo
      if (!userInfo) return

      try {
        // Always fetch newest clinic data (providers/patients/locations)
        await this.$store.dispatch('calendar/fetchClinic', {
          clinicId: userInfo.clinic_id
        })

        // Always get or create provider calendar to ensure it's up to date
        await this.$store.dispatch('calendar/getOrCreateCalendar', {
          calendar_owner_id: userInfo.customer_id,
          role: userInfo.user_roles?.includes('clinic') ? 'clinicadmin' : 'provider',
          practice_id: userInfo.clinic_id
        })

        // Now update patient data with full calendar info after data is loaded
        const patientId = this.currentPatientId
        if (patientId) {
          const updatedPatientList = this.$store.getters['calendar/formattedPatientsList']
          
          // Try to find patient with type-safe comparison (both string and number)
          const selectedPatient = updatedPatientList.find(p => 
            p.id == patientId || String(p.id) === String(patientId)
          )

          if (selectedPatient && this.scheduleActiveTab === 'task') {
            // Update task info with calendar_id
            const currentTaskInfo = this.$store.state.calendar.currentTaskInfo || {}
            this.$store.dispatch('calendar/setCurrentTaskInfo', {
              ...currentTaskInfo,
              calendar_id: selectedPatient.calendar_id
            })
          }
        }
      } catch (error) {
        console.error('Error loading calendar data in background:', error)
      }
    },
    async closeScheduleDetail() {
      this.showScheduleDetail = false
      // Clear the store state
      this.$store.dispatch('calendar/setCurrentTaskInfo', null)
      this.$store.dispatch('calendar/setCurrentEventInfo', null)
      
      // Wait for backend to process the save, then refetch calendar data
      if (this.currentPatientId) {
        try {
          // Brief delay to ensure backend has processed the save
          await new Promise(resolve => setTimeout(resolve, 500))
          
          // Refetch appointments and tasks to update the calendar tab
          await this.patientInfoStore.actions.fetchPatientAppointments(this.currentPatientId)
          await this.patientInfoStore.actions.fetchPatientTasks(this.currentPatientId)
        } catch (error) {
          console.error('Failed to refetch calendar data:', error)
          notify({ type: 'error', message: 'Failed to refresh calendar data' })
        }
      }
    },
    openSendDocumentDialog() {
      this.showSendDocumentDialog = true
    },
    openAddPatientDialog() {
      this.showAddPatientDialog = true
    },
    openComingSoonDialog() {
      this.showComingSoonDialog = true
    },
    openInternalNotesDialog() {
      this.showInternalNotesDialog = true
    },
    openNewOrderDialog() {
      if (!this.currentPatientId) {
        notify({
          type: 'warning',
          title: 'No Patient Selected',
          message: 'Please select a patient before placing an order.',
          duration: 3000,
          showClose: false,
        });
        return;
      }
      const patientBasicInfo = this.currentPatientData?.patientBasicInfo;
      if (!patientBasicInfo?.firstName || !patientBasicInfo?.lastName) {
        notify({
          type: 'warning',
          title: 'Incomplete Patient Info',
          message: 'Patient first name and last name are required before placing an order.',
          duration: 3000,
          showClose: false,
        });
        return;
      }
      this.showNewOrderDialog = true
      // sessionStorage.patient is read by VaOrderSummary / CheckoutDialog
      const writeSession = (raw) => {
        const patientData = buildPatientSession(raw);
        if (!patientData) return;
        sessionStorage.setItem("patient", JSON.stringify(patientData));
        this.$store.dispatch("orderTest/order/setPatient", patientData);
        this.$store.dispatch("orderTest/user/setPatientCountry", patientData.country || '');
        this.$store.dispatch("orderTest/setIsPatientInfoInSessionStorage");
      };
      const a = patientBasicInfo.addressObj || {};
      const cachedAsRaw = {
        patient_id: patientBasicInfo.patientId,
        patient_first_name: patientBasicInfo.firstName,
        patient_last_name: patientBasicInfo.lastName,
        patient_birthdate: patientBasicInfo.dob,
        patient_gender: patientBasicInfo.gender,
        patient_contact: [
          { contact_description: 'email', contact_details: patientBasicInfo.email || '' },
          { contact_description: 'phone', contact_details: patientBasicInfo.phone || '' },
        ],
        patient_address: [{
          country: a.country || '',
          state: a.state || '',
          zipcode: a.zipCode || '',
          city: a.city || '',
          street_address: a.streetAddress || '',
        }],
      };
      getPatientByIdApi(this.currentPatientId)
        .then(({ data }) => writeSession(data))
        .catch((err) => {
          console.error("Order session: API fetch failed, falling back to cached patient:", err);
          writeSession(cachedAsRaw);
        });
    },
    openMedicationFormDialog() {
      this.editingMedication = null;
      this.showMedicationFormDialog = true;
      // Set flag: if list was open, return to it after form closes
      this.medicationFormFromList = this.showMedicationsListDialog;
      this.showMedicationsListDialog = false;  // Close list dialog
    },
    handleEditFromListDialog(medication) {
      this.editingMedication = medication;
      this.medicationFormFromList = true;  // Mark as coming from list
      this.showMedicationFormDialog = true;
      this.showMedicationsListDialog = false;  // Close list dialog
    },
    handleMedicationFormClose() {
      this.showMedicationFormDialog = false;
      this.editingMedication = null;
      // Only reopen list dialog if form was opened from list
      if (this.medicationFormFromList) {
        this.showMedicationsListDialog = true;
      }
      this.medicationFormFromList = false;  // Reset flag
    },
    handleEditMedication(medication) {
      // Fetch all medications for the dialog if needed
      if (this.allMedications.length === 0 && !this.medicationPagination.isLoading) {
        this.fetchAllMedications();
      }
      // Reset to page 1 when opening dialog
      this.medicationPagination.currentPage = 1;
      this.showMedicationsListDialog = true;
    },
    async handleDiscontinueMedication(medication) {
      if (!this.currentPatientId) return;

      try {
        // Call API to update is_active to false
        await updateMedicationRecord(this.currentPatientId, medication.id, { is_active: false });

        // Success notification
        notify({
          title: 'Success',
          message: 'Medication Discontinued Successfully',
          type: 'success',
          duration: 3000
        });

        // Remove from local list
        const index = this.allMedications.findIndex(m => m.id === medication.id);
        if (index > -1) {
          this.allMedications.splice(index, 1);
        }

        // Update total count
        this.medicationPagination.total = Math.max(0, this.medicationPagination.total - 1);
      } catch (error) {
        console.error('Failed to discontinue medication:', error);
        notify({
          title: 'Error',
          message: 'Failed to Discontinue Medication',
          type: 'error',
          duration: 3000
        });
      }
    },
    handleMedicationSave(medicationData) {
      if (this.editingMedication) {
        // Update existing
        const index = this.allMedications.findIndex(m => m.id === this.editingMedication.id);
        if (index > -1) {
          this.allMedications[index] = { ...medicationData, id: this.editingMedication.id };
        }
      } else {
        // Add new - will refresh from API after form closes
        this.allMedications.push({
          ...medicationData,
          id: 'med_' + Date.now()
        });
      }
      this.showMedicationFormDialog = false;
      this.editingMedication = null;  // Reset for next use
      // Refresh all medications from API
      this.fetchAllMedications();
      // Only reopen list dialog if form was opened from list
      if (this.medicationFormFromList) {
        this.showMedicationsListDialog = true;
      }
      this.medicationFormFromList = false;  // Reset flag
    },
    // Fetch all medications using store action (shared state)
    async fetchAllMedications() {
      if (!this.currentPatientId) return;
      this.medicationPagination.isLoading = true;

      try {
        await this.patientInfoStore.actions.fetchCurrentPatientMedications(this.currentPatientId);
        this.medicationPagination.total = this.allMedications.length;
      } catch (error) {
        console.error('Failed to fetch all medications:', error);
      } finally {
        this.medicationPagination.isLoading = false;
      }
    },
    handleMedicationPageChange(page) {
      // Client-side pagination - just update the page, dialog handles it
      this.medicationPagination.currentPage = page;
    },
    handleMedicationPageSizeChange(pageSize) {
      this.medicationPagination.pageSize = pageSize;
      this.medicationPagination.currentPage = 1;
    },
    handleMedicationDeleted(medicationId) {
      // Remove from store shared state
      this.patientInfoStore.actions.removeMedication(medicationId);
      // Update total count
      this.medicationPagination.total = Math.max(0, this.medicationPagination.total - 1);
    },
    // Allergy dialog methods
    openAllergyFormDialog() {
      this.editingAllergy = null;
      this.showAllergyFormDialog = true;
      // Set flag: if list was open, return to it after form closes
      this.allergyFormFromList = this.showAllergiesListDialog;
      this.showAllergiesListDialog = false;  // Close list dialog
    },
    async handleAllergyFormSave(allergyData) {
      // Refresh allergies from API after save
      await this.fetchAllAllergies();

      this.showAllergyFormDialog = false;
      this.editingAllergy = null;
      // Only reopen list dialog if form was opened from list
      if (this.allergyFormFromList) {
        this.showAllergiesListDialog = true;
      }
      this.allergyFormFromList = false;  // Reset flag
    },
    // Fetch all allergies using store action (shared state)
    async fetchAllAllergies() {
      if (!this.currentPatientId) return;

      try {
        await this.patientInfoStore.actions.fetchCurrentPatientAllergies(this.currentPatientId);
      } catch (error) {
        console.error('Failed to fetch allergies:', error);
      }
    },
    handleAllergyFormClose() {
      this.showAllergyFormDialog = false;
      this.editingAllergy = null;
      // Only reopen list dialog if form was opened from list
      if (this.allergyFormFromList) {
        this.showAllergiesListDialog = true;
      }
      this.allergyFormFromList = false;  // Reset flag
    },
    async handleAllergyDeleted() {
      // Refresh allergies from API after delete
      await this.fetchAllAllergies();
    },
    handleEditAllergy() {
      // Open the allergies list dialog
      this.showAllergiesListDialog = true;
    },
    handleEditFromAllergyListDialog(allergy) {
      this.editingAllergy = allergy;
      this.allergyFormFromList = true;  // Mark as coming from list
      this.showAllergyFormDialog = true;
      this.showAllergiesListDialog = false;  // Close list dialog
    },
    // Health Info dialog methods
    openHealthInfoDialog() {
      // Always open form dialog directly for "Add new"
      this.showHealthInfoDialog = true;
    },
    handleHealthInfoDialogClose() {
      this.showHealthInfoDialog = false;
    },
    async fetchPatientVitals() {
      if (!this.currentPatientId) return;
      try {
        await this.patientInfoStore.actions.fetchCurrentPatientVitals(this.currentPatientId);
      } catch (error) {
        console.error('Failed to fetch vitals:', error);
      }
    },
    async fetchBillingOrders() {
      if (!this.currentPatientId) return;
      try {
        await this.patientInfoStore.actions.fetchCurrentPatientBillingOrders(this.currentPatientId);
      } catch (error) {
        console.error('Failed to fetch billing orders:', error);
      }
    },
    async fetchPatientDocuments() {
      if (!this.currentPatientId) return;
      try {
        await this.patientInfoStore.actions.fetchCurrentPatientDocuments(this.currentPatientId);
      } catch (error) {
        console.error('Failed to fetch patient documents:', error);
      }
    },
    async handleHealthInfoSave() {
      // Dialog already called API and updated store; computed will auto-update
      this.showHealthInfoDialog = false;

      notify({
        title: 'Success',
        message: 'Health Information Added Successfully',
        type: 'success',
        duration: 3000
      });
    },
    initializeFakeReconcileData() {
      // Initialize fake reconcile data for testing
      this.reconcileData = { ...fakeReconcileData };
    },
    // Reconcile dialog methods
    handleReconcile() {
      console.log('handleReconcile called, showReconcileDialog:', this.showReconcileDialog);
      this.showReconcileDialog = true;
      console.log('after set to true:', this.showReconcileDialog);
    },
    handleOpenVariableDialog(variables) {
      this.currentPtVariables = variables;
      this.showPtVariablesDialog = true;
    },
    handleReconcileAddSelected(selectedItems) {
      // Add selected new medications to store
      if (selectedItems.newMedications && selectedItems.newMedications.length > 0) {
        const updatedMedications = [...this.allMedications, ...selectedItems.newMedications];
        this.$store.commit('myPatients/patientInfo/SET_CURRENT_PATIENT_MEDICATIONS', updatedMedications);
      }

      // Add selected new allergies to store
      if (selectedItems.newAllergies && selectedItems.newAllergies.length > 0) {
        const updatedAllergies = [...this.allergies, ...selectedItems.newAllergies];
        this.$store.commit('myPatients/patientInfo/SET_CURRENT_PATIENT_ALLERGIES', updatedAllergies);
      }

      // Remove selected items from reconcile data
      this.reconcileData.newMedications = this.reconcileData.newMedications.filter(
        m => !selectedItems.newMedications.some(sm => sm.id === m.id)
      );
      this.reconcileData.newAllergies = this.reconcileData.newAllergies.filter(
        a => !selectedItems.newAllergies.some(sa => sa.id === a.id)
      );

      // Close dialog
      this.showReconcileDialog = false;
    },
    // Workflow dialog handlers
    handleWorkflowPreview(workflowId) {
      console.log('Preview workflow:', workflowId);
      // Navigate to workflow editor in preview mode
      this.$router.push(`/my-workbench/editor/${workflowId}?mode=preview`);
    },
    handleWorkflowTogglePause(workflowId) {
      console.log('Toggle pause workflow:', workflowId);
      // TODO: Implement pause/resume workflow for patient
      notify({
        title: 'Info',
        message: 'Workflow pause/resume functionality will be implemented',
        type: 'info',
        showClose: true
      });
    },
    handleWorkflowRemove(workflowId) {
      console.log('Remove patient from workflow:', workflowId);
      // TODO: Implement remove patient from workflow
      notify({
        title: 'Info',
        message: 'Remove patient from workflow functionality will be implemented',
        type: 'info',
        showClose: true
      });
    },
    // handleSendTask and handleCreateAppointment removed - using ScheduleDetail from calendar
    handleSendDocument(documentData) {
      // Add document card to messages
      const documentMessage = {
        type: 'card',
        id: Date.now(),
        sender: 'provider',
        cardType: 'document',
        title: 'Documents Shared',
        description: `${documentData.documents.length + documentData.uploadedFiles.length} document(s) shared`,
        timestamp: new Date()
      }
      this.messages.push(documentMessage)
      this.scrollToBottom()
    },
    handleAddPatient(patientData) {
      // Patient added successfully
    },
    handleInternalNoteSubmit(text) {
      // Internal note submitted
    },
    async handleNewOrder(orderResult) {
      if (!orderResult?.success) return;

      try {
        const firstItem = Array.isArray(orderResult.items) && orderResult.items.length > 0
          ? orderResult.items[0]
          : null;
        const testName = firstItem?.name || 'Order';

        // Create a new Billing Sample in the store and pin it immediately
        const now = new Date();
        const pad2 = (n) => String(n).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const serviceDate = `${monthNames[now.getMonth()]}-${pad2(now.getDate())}-${now.getFullYear()}`;
        const sampleId = orderResult.sampleId || String(Math.floor(100000 + Math.random() * 900000));

        const sampleMessage = {
          id: `sample_${sampleId}`,
          serviceDate,
          status: 'Order Created',
          statusDescription: 'The order has been successfully created in the system.',
          sampleId: sampleId,
          orderTestNames: testName,
          samplePlacedDate: `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`,
          questionnaireStatus: '–',
          questionnaireDescription: '–'
        };

        // Commit sample to Billing.Sample
        this.$store.commit('patientInfo/ORDER_PLACED_SAMPLE', ({ sampleMessage, patientId: this.currentPatientId }));

        // TODO: Re-enable pegboard pin after backend API is fixed
        // await this.$store.dispatch('myPatients/patientInfo/pinCard', {
        //   customer_id: this.$store.state.login.userInfo.customer_id,
        //   clinic_id: this.$store.state.login.userInfo.clinic_id,
        //   patient_id: this.currentPatientId,
        //   cardData: {
        //     id: sampleMessage.id,
        //     category: 'Billing',
        //     entity_type: 'sample',
        //     entity_id: sampleMessage.sampleId,
        //     metadata: {
        //       status: sampleMessage.status,
        //       sampleName: testName,
        //       serviceDate: serviceDate
        //     }
        //   }
        // });

        // await this.getPegBoardList();

        // Refresh billing orders in store so BillingOrderCard updates automatically
        await this.fetchBillingOrders();

        console.log('✅ New order processed:', { orderId: orderResult.orderId, sampleMessage });
      } catch (err) {
        console.error('❌ Failed to handle new order UI update:', err);
      }
    },
    async sendMessage() {
      // Prevent sending if already sending or no content
      if (this.isSendingMessage || (!this.newMessage.trim() && this.droppedImages.length === 0)) {
        return;
      }
      
      // Disable send button immediately
      this.isSendingMessage = true;
      console.log('🚫 Send button disabled to prevent duplicates');
      
      const messageText = this.newMessage.trim();
      
      // Prepare attachments for uploaded images
      const imageAttachments = this.droppedImages
        .filter(img => img.uploadedData) // Only include successfully uploaded images
        .map(img => {
          const data = img.uploadedData;
          // Convert mime_type to uppercase file type (e.g., "image/jpeg" -> "JPEG")
          const fileType = data.mime_type.split('/')[1].toUpperCase();
          
          return {
            "text": "This is an image",
            "title": "image_uploaded",
            "fields": [
              {
                "title": "display_format",
                "value": "image"
              },
              {
                "title": "id",
                "value": data.id
              },
              {
                "title": "name",
                "value": data.file_name
              },
              {
                "title": "created",
                "value": data.created_at
              },
              {
                "title": "ftype",
                "value": fileType
              },
              {
                "title": "size",
                "value": String(data.size_bytes)
              }
            ]
          };
        });
      
      // Prepare message body for API
      let messageBody = {
        "message": {
          "rid": this.roomId,
          "msg": messageText,
          "alias": "",
          "emoji": "",
          "avatar": "",
          "attachments": imageAttachments
        }
      };
      
      // Add t='image' if there are images
      if (imageAttachments.length > 0) {
        messageBody.message.t = "image";
      }
      
      try {
        // Send message via API (images already uploaded)
        const response = await this.RocketChatService.post('/messages', messageBody);
        
        if (response.status === 201 && response.data && response.data.success) {
          const responseMessage = response.data.message;
          const messageId = responseMessage._id;
          const roomId = responseMessage.rid;
          
          
          // Check if message already exists in sent messages store
          const existingSentMessage = this.$store.getters['myPatients/message/getSentMessage'](roomId, messageId);
          
          if (existingSentMessage) {
            console.log('🔄 Message already exists in sent messages, skipping...');
          } else {

            // Add to sent messages first
            this.$store.dispatch('myPatients/message/addSentMessage', {
              roomId: roomId,
              messageId: messageId,
              message: responseMessage
            });

            // Then add to room messages
            this.$store.dispatch('myPatients/message/addMessage', {
              roomId: roomId,
              message: responseMessage
            });

            // Increment total when user successfully sends a message
            this.$store.dispatch('myPatients/message/incrementRoomTotal', roomId);
          }

          // ✅ SUCCESS: Clear input, images, and scroll
          this.newMessage = '';
          this.droppedImages = [];
          this.scrollToBottom();
        }
      } catch (error) {

        // Add failed message to local history
        const failedMessage = {
          _id: uuidv4(),
          rid: this.roomId,
          msg: messageText,
          ts: new Date().toISOString(),
          u: {
            _id: this.rc_user_id,
            username: this.$store.state.login.userInfo?.username || 'user'
          },
          t: undefined,
          failed: true,
          failedAt: new Date().toISOString()
        };

        // Add to local store with failed flag
        this.$store.dispatch('myPatients/message/addMessage', {
          roomId: this.roomId,
          message: failedMessage
        });

        // Clear input after adding to failed messages
        this.newMessage = '';
        this.scrollToBottom();

        // Show notification
        notify({
          type: 'error',
          message: 'Failed to send message. You can retry by clicking the message.'
        });
      } finally {
        // ✅ Always re-enable send button
        this.isSendingMessage = false;
      }
    },

    getSenderName(messageData) {
      // Get sender name for display
      if (!messageData.u) return 'Unknown';

      // Current user
      if (messageData.u._id === this.rc_user_id) {
        return this.$store.getters['login/userInfo']?.customer_name || 'You';
      }

      // Bot message
      if (messageData.u._id === 'oafKicny6TymdzNxQ' ||
          messageData.u._id === 'EuMKAsjDZTsLc74Kw') {
        return 'System';
      }

      // Other user - try to get from practice info
      const username = messageData.u?.username;
      if (username && username.startsWith('doctor_')) {
        const parts = username.split('_');
        if (parts.length >= 3) {
          const customerId = parseInt(parts[1]);
          const customer = this.$store.getters['myPatients/practiceInfo/getCustomerById'](customerId);
          if (customer) {
            const firstName = customer.customer_first_name || '';
            const lastName = customer.customer_last_name || '';
            const suffix = customer.customer_suffix || '';
            return suffix ? `${firstName} ${lastName}, ${suffix}`.trim() : `${firstName} ${lastName}`.trim();
          }
        }
      }

      // Fallback
      return 'Other';
    },

    handleEnterKey(event) {
      if (!event.shiftKey) {
        this.sendMessage()
      }
    },
    
    scrollToBottom() {
      // Use nextTick to ensure DOM is updated before scrolling
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          // Smooth scroll to bottom for better UX
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          })
          
          // Fallback for browsers that don't support smooth scroll
          if (container.scrollTop === 0) {
            container.scrollTop = container.scrollHeight
          }
        }
      })
    },
  }
}
</script>

<style scoped>
/* Chat composer Send button (ResponsiveButton) overrides */
.chat-send-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-top-color: transparent;
  border-radius: 9999px;
  animation: chat-send-spin 0.8s linear infinite;
}
@keyframes chat-send-spin {
  to { transform: rotate(360deg); }
}

.chat-window {
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  font-family: 'Inter', sans-serif;
  width: 100%;
}

.header-section {
  background-color: #F9FBFD;
  position: relative;
}

.patient-header {
  background-color: #F9FBFD;
  padding: 0 48px;
}

.patient-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.patient-avatar {
  position: relative;
  width: 48px;
  height: 48px;
}

.avatar-female {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  position: absolute;
  left: 0;
  top: 0;
}

.avatar-male {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  position: absolute;
  right: -8px;
  bottom: 0;
  border: 2px solid white;
}

.patient-details {
  flex: 1;
}

.patient-name {
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  color: #333333;
  margin: 0 0 4px 0;
}

.patient-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666666;
}

.patient-bio, .patient-dob {
  font-weight: 400;
}

.patient-separator {
  color: #cccccc;
  margin: 0 4px;
}

.tab-navigation {
  display: flex;
  background: #F9FBFD;
  padding: 0px 48px;
  gap: 10px;
  position: relative;
  z-index: 5;
  height: 36px;
  align-items: flex-start;
}

.tab-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: white;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  height: 36px;
  box-shadow: 0 -6px 15px 0 rgba(74, 74, 74, 0.15);
  transition: all 0.2s ease;
}

.tab-container.active {
  min-width: 300px;
  z-index: 20;
  box-shadow: 0 0px 15px 0 rgba(74, 74, 74, 0.15);
}

.tab-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
}

.tab-header.active {
  color: #666;
}

.tab-container.active .tab-header {
  border-radius: 8px 8px 0 0;
}

.tab-pin-cards {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 400px;
  overflow-y: auto;
  padding: 16px;
  background: white;
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 10px 15px 0 rgba(74, 74, 74, 0.15);
  z-index: 100;
  animation: expandDown 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  scrollbar-width: thin;
  scrollbar-color: #d4d4d4 transparent;
}

.tab-pin-cards::-webkit-scrollbar {
  width: 4px;
}

.tab-pin-cards::-webkit-scrollbar-track {
  background: transparent;
}

.tab-pin-cards::-webkit-scrollbar-thumb {
  background-color: #d4d4d4;
  border-radius: 4px;
}

.tab-pin-cards::-webkit-scrollbar-thumb:hover {
  background-color: #b0b0b0;
}

@keyframes expandDown {
  from {
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    opacity: 0;
  }
  to {
    max-height: 400px;
    padding-top: 16px;
    padding-bottom: 16px;
    opacity: 1;
  }
}

.tab-icon {
  width: 18px;
  height: 18px;
  fill: currentColor;
}

.tab-label {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.pin-cards-header {
  display: none;
}

.pin-cards-count {
  font-size: 12px;
  color: #666666;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 12px;
}

.pin-cards-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.no-pins {
  text-align: center;
  color: #999999;
  font-size: 12px;
  padding: 16px 0;
}

.tab-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  width: 100%;
}

.tab-loading-spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #E5E7EB;
  border-top-color: #1E4670;
  border-radius: 50%;
  animation: tab-spin 0.8s linear infinite;
  margin-bottom: 12px;
}

@keyframes tab-spin {
  to { transform: rotate(360deg); }
}

.tab-loading-message {
  color: #9A9A9A;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 400;
  margin: 0;
}

.tab-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
  width: 100%;
}

.tab-empty-illustration {
  width: 116px;
  height: 117px;
  margin-bottom: 16px;
}

.tab-empty-message {
  color: #9A9A9A;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
  max-width: 236px;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px 48px;
  margin-top: 24px;
  background-color: #ffffff;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 100%;
}

/* Loading More Messages */
.loading-more-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  color: #666;
  font-size: 14px;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #004879;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}


/* Custom Element Plus styling */
:deep(.el-textarea .el-textarea__inner) {
  border-radius: 8px;
  border: 1px solid #e8e9ea;
  padding: 12px 50px 12px 12px;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  resize: none;
  min-height: 80px !important;
}

:deep(.el-textarea .el-textarea__inner:focus) {
  border-color: #1890ff;
}

:deep(.el-switch .el-switch__core) {
  background-color: #dcdfe6;
}

:deep(.el-switch.is-checked .el-switch__core) {
  background-color: #1890ff;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .left-panel {
    width: 280px;
  }
}

@media (max-width: 768px) {
  .content-container {
    flex-direction: column;
  }
  
  .left-panel {
    width: 100%;
    height: 200px;
  }
  
  .pin-cards-section {
    display: none;
  }
  
  .tab-navigation {
    flex: 1;
  }
  
}
.message-outer{
  border: 1px solid #c4c4c4
}

/* Image preview scrollbar styling for webkit browsers */
.overflow-x-auto::-webkit-scrollbar {
  height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #c4c4c4;
  border-radius: 3px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>

<style>
/* Global styles for the popover */
.other-actions-popover {
  padding: 8px !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.other-actions-popover .el-popper__arrow {
  display: none !important;
}
</style>
